import React from 'react'
import {fmN, fmP} from '../../helpers/format'
import {tipHandlers, useTooltip} from './Tooltip'

const MONO = 'SFMono-Regular,Consolas,monospace'
// viewBox cố định: chữ co theo cột (tối thiểu 300px nên còn khoảng 9px), đổi lại nhãn luôn vừa khung không cần đo
const W = 420, H = 250, CX = W / 2, CY = H / 2, R = 72
// Nhãn hai dòng (tên, %): khoảng cách tối thiểu giữa hai nhãn cùng phía để không đè nhau
const LABEL_GAP = 28
const NAME_MAX = 15

const _point = (radius, angle) => [CX + radius * Math.sin(angle), CY - radius * Math.cos(angle)]

const _slicePath = (start, end) => {
    const [x0, y0] = _point(R, start), [x1, y1] = _point(R, end)
    const large = end - start > Math.PI ? 1 : 0

    return `M${CX} ${CY} L${x0} ${y0} A${R} ${R} 0 ${large} 1 ${x1} ${y1} Z`
}

const _short = (name) => name.length > NAME_MAX ? `${name.slice(0, NAME_MAX - 1)}…` : name

// Dàn nhãn một phía theo thứ tự từ trên xuống, đẩy xuống khi chạm nhãn trước, rồi kéo cả cụm lên nếu tràn đáy
const _spread = (labels) => {
    const sorted = [...labels].sort((a, b) => a.y - b.y)
    sorted.forEach((label, index) => {
        if (index && label.y - sorted[index - 1].y < LABEL_GAP) label.y = sorted[index - 1].y + LABEL_GAP
    })
    const overflow = sorted.length ? sorted[sorted.length - 1].y - (H - 20) : 0
    if (overflow > 0) sorted.forEach(label => { label.y = Math.max(16, label.y - overflow) })

    return sorted
}

/**
 * Biểu đồ tròn cơ cấu (phần/tổng), nhãn ghi thẳng bên cạnh từng lát để không phải dò màu ở chú thích.
 * slices: [{key, label, value, share, color}] đã sắp giảm dần
 */
const SharePie = ({slices, aria}) => {
    const tooltip = useTooltip()
    const shown = slices.filter(slice => slice.value > 0)

    if (!shown.length) return <div className="empty">Không có dữ liệu.</div>

    let angle = 0
    const arcs = shown.map(slice => {
        const start = angle
        angle += slice.share * 2 * Math.PI
        const mid = (start + angle) / 2
        const [ax, ay] = _point(R, mid)
        const [bx, by] = _point(R + 14, mid)

        return {...slice, start, end: angle, ax, ay, bx, y: by, right: Math.sin(mid) >= 0}
    })
    const labels = [..._spread(arcs.filter(arc => arc.right)), ..._spread(arcs.filter(arc => !arc.right))]

    return (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={aria || 'cơ cấu lỗi'} className="share-pie">
            {arcs.map(slice => {
                const fill = `var(${slice.color})`
                const tip = tipHandlers(tooltip, (
                    <>
                        <div><b>{slice.label}</b></div>
                        <div><span className="k">Tỉ trọng</span> <b>{fmP(slice.share)}</b></div>
                        <div><span className="k">Lượt ghi nhận</span> <b>{fmN(slice.value)}</b></div>
                    </>
                ))

                // Một lát chiếm trọn vòng: cung 360° không vẽ được bằng path
                return slice.share >= 0.9999
                    ? <circle key={slice.key} cx={CX} cy={CY} r={R} fill={fill} style={{cursor: 'pointer'}} {...tip}/>
                    : <path key={slice.key} d={_slicePath(slice.start, slice.end)} fill={fill} stroke="var(--panel)" strokeWidth="1.5" style={{cursor: 'pointer'}} {...tip}/>
            })}
            {labels.map(label => {
                const ex = label.right ? CX + R + 30 : CX - R - 30
                const tx = label.right ? ex + 4 : ex - 4
                const anchor = label.right ? 'start' : 'end'

                return (
                    <g key={`l-${label.key}`} pointerEvents="none">
                        <polyline points={`${label.ax},${label.ay} ${label.bx},${label.y} ${ex},${label.y}`} fill="none" stroke="var(--ink-3)" strokeWidth="1"/>
                        <text x={tx} y={label.y - 3} textAnchor={anchor} fontSize="12" fill="var(--ink)">
                            {_short(label.label)}
                            <title>{label.label}</title>
                        </text>
                        <text x={tx} y={label.y + 11} textAnchor={anchor} fontSize="12" fontFamily={MONO} fontWeight="700" fill="var(--ink)">
                            ({fmP(label.share)})
                        </text>
                    </g>
                )
            })}
        </svg>
    )
}

export default SharePie
